import { Box, BoxProps, Flex, HStack, Stack, Tag } from '@chakra-ui/react';
import {
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { Button } from '@main/complykit/components/button';
import { Callout } from '@main/complykit/components/callout';
import { Card } from '@main/complykit/components/card';
import { IconButton } from '@main/complykit/components/icon-button';
import { Content, Footer, Header } from '@main/complykit/components/slots';
import { Text } from '@main/complykit/components/text';
import {
  QuestionnaireComponentKind,
  QuestionnaireFormComponentRegistry,
  SectionFieldProps,
} from '@main/dynamic-form';
import {
  DynamicField,
  DynamicFormContextType,
  useDynamicFormContext,
} from '@main/dynamic-form/react';
import { Vendor_Questionnaire_Form_Answer_Statuses_Enum } from '@main/graphql/types.generated';
import { MarkdownEditor } from '@main/ui';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useQuestionnnaireFormContext } from '../context';
import { QuestionnaireFormFieldConfig, QuestionnnaireFieldWrapperProps } from '../form-types';
import { useVisibleFields } from '../use-visible-fields';
import { createNewAnswerFor } from '../utils';

export function SectionField({ config }: SectionFieldProps) {
  const { t } = useTranslation();
  const { trigger, getFieldState } = useFormContext();
  const formContext = useDynamicFormContext<
    QuestionnaireFormComponentRegistry,
    QuestionnnaireFieldWrapperProps
  >();
  const { answersMap } = useQuestionnnaireFormContext();
  const { visibleFields } = useVisibleFields(config.fields);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [invalidQuestions, setInvalidQuestions] = useState<string>('');
  const sections = useMemo(
    () =>
      formContext.wrapperProps?.visibleFields.filter(
        (field) => field.kind === QuestionnaireComponentKind.Section,
      ),
    [formContext.wrapperProps?.visibleFields],
  );
  const sectionIdx = useMemo(
    () => sections?.findIndex((section) => section.name === config.name) ?? -1,
    [config.name, sections],
  );
  const fieldContext = useMemo(
    () => ({
      ...formContext,
      wrapperProps: { ...formContext?.wrapperProps, visibleFields, isCollapsed },
    }),
    [formContext, isCollapsed, visibleFields],
  ) as DynamicFormContextType<QuestionnaireFormComponentRegistry, QuestionnnaireFieldWrapperProps>;
  const pendingFields = useMemo(
    () =>
      visibleFields.filter(
        (field) =>
          !answersMap.has(field.name) ||
          answersMap.get(field.name)?.status ===
            Vendor_Questionnaire_Form_Answer_Statuses_Enum.Pending,
      ),
    [answersMap, visibleFields],
  );

  async function completeAll() {
    const isValid = await trigger(pendingFields.map((field) => field.name));

    if (!isValid) {
      return setInvalidQuestions(
        pendingFields
          .filter((field) => getFieldState(field.name).invalid)
          .map((field) => visibleFields.indexOf(field) + 1)
          .join(', '),
      );
    } else {
      setInvalidQuestions('');
    }

    await Promise.all(
      // eslint-disable-next-line @typescript-eslint/await-thenable
      pendingFields.map((field) =>
        formContext.wrapperProps?.onAnswerChange?.(
          answersMap.get(field.name) ?? createNewAnswerFor(field),
          { status: Vendor_Questionnaire_Form_Answer_Statuses_Enum.Completed },
        ),
      ),
    );
  }

  const itemRender = useCallback(
    (field: QuestionnaireFormFieldConfig) => <DynamicField {...fieldContext} config={field} />,
    [fieldContext],
  );

  return (
    <SectionFieldSkeleton
      headerLeft={
        <Tag colorScheme="purple" size="md">
          {t('questionnaires.form.sectionNumber', {
            number: sectionIdx + 1,
            total: sections?.length,
          })}
        </Tag>
      }
      headerCenter={
        isCollapsed && (
          <Text weight="medium" lines={1} isGrow>
            {config.label}
          </Text>
        )
      }
      headerActions={
        <>
          <IconButton
            variant="outline"
            icon={isCollapsed ? ArrowsPointingOutIcon : ArrowsPointingInIcon}
            label={
              isCollapsed
                ? t('questionnaires.form.expandSection')
                : t('questionnaires.form.collapseSection')
            }
            onPress={() => setIsCollapsed((v) => !v)}
          />
          {pendingFields.length > 0 &&
            (isCollapsed ? (
              <IconButton
                variant="solid"
                icon={CheckIcon}
                label={t('questionnaires.form.completeSection')}
                onPress={() => completeAll()}
              />
            ) : (
              <Button onPress={() => completeAll()}>
                {t('questionnaires.form.completeSection')}
              </Button>
            ))}
        </>
      }
    >
      {!isCollapsed && (
        <Stack spacing="1" mb="4">
          <Text weight="medium">{config.label}</Text>
          {config.description && (
            <MarkdownEditor
              variant="unstyled"
              config={{ editable: false }}
              value={config.description}
              boxProps={{ color: 'gray.500' }}
              contentEditableProps={{ style: { fontSize: 'var(--chakra-fontSizes-sm)' } }}
            />
          )}
        </Stack>
      )}
      <Flex flexDir="column" gap="3">
        {invalidQuestions && isCollapsed && (
          <Callout
            status="error"
            onDismiss={() => setInvalidQuestions('')}
            dismissLabel={t('buttons.close')}
          >
            {t('questionnaires.form.incompleteSectionError.content', {
              questions: invalidQuestions,
            })}
          </Callout>
        )}
        <formContext.itemsRender fields={visibleFields} itemRender={itemRender} />
      </Flex>
    </SectionFieldSkeleton>
  );
}

export interface SectionFieldSkeletonProps {
  headerLeft?: ReactNode;
  headerCenter?: ReactNode;
  headerActions?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  /** The drop target the drag is currently over. */
  isActive?: boolean;
  /** The drop is not permitted here. */
  isConstrained?: boolean;
}

export function SectionFieldSkeleton({
  headerLeft,
  headerCenter,
  headerActions,
  children,
  footer,
  isActive,
  isConstrained,
  p,
  ...box
}: SectionFieldSkeletonProps & BoxProps) {
  /* Drag feedback is an interaction state, not a card appearance: it is
   * transient, it belongs to the drag interaction rather than the content,
   * and it reads as an outline the way selection does elsewhere. */
  const outline = isConstrained
    ? 'var(--chakra-colors-orange-300)'
    : isActive
      ? 'var(--chakra-colors-blue-500)'
      : undefined;

  /* A caller's padding is the card's inset, not a margin around it. The
   * skeleton is used at two densities: a section holding question cards, and
   * a single question inside one. Sending the value to the wrapper instead
   * would leave the card paying its own inset as well, so the tighter of the
   * two would read as a gap outside plus a full inset within. */
  const padding = p === '3' || p === 3 ? 'compact' : 'default';

  return (
    <Box
      transition="outline-color 0.2s ease-out"
      outline={outline ? '2px solid' : undefined}
      outlineColor={outline}
      outlineOffset="-1px"
      borderRadius="var(--ck-radius-3)"
      data-active={isActive || undefined}
      data-constrained={isConstrained || undefined}
      {...box}
    >
      <Card padding={padding}>
        <Header>
          <HStack alignItems="center" justifyContent="space-between">
            <Box flexShrink="0">{headerLeft}</Box>
            {headerCenter && (
              <Box flexGrow="1" fontSize="md" fontWeight="500">
                {headerCenter}
              </Box>
            )}
            <HStack flexShrink="0">{headerActions}</HStack>
          </HStack>
        </Header>
        {children && <Content>{children}</Content>}
        {footer && <Footer>{footer}</Footer>}
      </Card>
    </Box>
  );
}
